import { TFunction } from 'i18next';
import { WebhookInfo, WebhookEvent } from 'common/types/api';

export enum WebhookInvalidReason {
	URL_BLACKLISTED = 'URL_BLACKLISTED',
	URL_NOT_HTTPS = 'URL_NOT_HTTPS',
	URL_NOT_VALID = 'URL_NOT_VALID',
	LABEL_NOT_EXISTING = 'LABEL_NOT_EXISTING',
	LABEL_TOO_LONG = 'LABEL_TOO_LONG',
	LABEL_BLACKLISTED = 'LABEL_BLACKLISTED',
	EVENT_NOT_VALID = 'EVENT_NOT_VALID',
	EVENT_BLACKLISTED = 'EVENT_BLACKLISTED',
	TOO_MANY_URLS = 'TOO_MANY_URLS',
	URLS_NOT_EXISTING = 'URLS_NOT_EXISTING',
	UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}

export const LABEL_MAX_LENGTH = 200;

export const translateWebhookValidationError = (
	error: WebhookInvalidReason,
	input: Partial<WebhookInfo>,
	t: TFunction,
) => {
	switch (error) {
		case WebhookInvalidReason.URL_BLACKLISTED: {
			return t(
				'common:Webhooks.ValidationErrors.urlBlacklisted',
				'You have already added that URL',
			);
		}
		case WebhookInvalidReason.URL_NOT_HTTPS: {
			return t(
				'common:Webhooks.ValidationErrors.urlNotHttps',
				'Webhook URLs must begin with https://',
			);
		}
		case WebhookInvalidReason.URL_NOT_VALID: {
			return t('common:Webhooks.ValidationErrors.urlNotValid', 'Please enter a valid URL');
		}
		case WebhookInvalidReason.LABEL_NOT_EXISTING: {
			return t('common:Webhooks.ValidationErrors.labelNotExisting', 'Webhooks must have a label');
		}
		case WebhookInvalidReason.LABEL_TOO_LONG: {
			return t('common:Webhooks.ValidationErrors.labelTooLong', {
				labelMaxLength: LABEL_MAX_LENGTH,
				defaultValue: 'Webhook labels can be at most {{labelMaxLength}} characters long',
			});
		}
		case WebhookInvalidReason.LABEL_BLACKLISTED: {
			return t('common:Webhooks.ValidationErrors.labelBlacklisted', {
				label: input.label,
				defaultValue: 'You already have another webhook with the label {{label}}',
			});
		}
		case WebhookInvalidReason.EVENT_NOT_VALID: {
			return t('common:Webhooks.ValidationErrors.eventNotValid', {
				event: input.event,
				defaultValue: '{{event}} is not a valid webhook event',
			});
		}
		case WebhookInvalidReason.EVENT_BLACKLISTED: {
			return t('common:Webhooks.ValidationErrors.eventBlacklisted', {
				event: input.event,
				defaultValue: 'The webhook event {{event}} is not allowed',
			});
		}
		case WebhookInvalidReason.URLS_NOT_EXISTING: {
			return t(
				'common:Webhooks.ValidationErrors.urlsNotExisting',
				'Webhooks must have at least one URL',
			);
		}
		case WebhookInvalidReason.TOO_MANY_URLS: {
			return t(
				'common:Webhooks.ValidationErrors.tooManyUrls',
				'A single webhook can have at most 5 URLs',
			);
		}
		default: {
			return t('common:Webhooks.ValidationErrors.unknownError', 'Webhook validation failed');
		}
	}
};

export const translateWebhookEvent = (event: WebhookEvent, t: TFunction) => {
	switch (event) {
		case WebhookEvent.ORDER_CREATED:
			return t('common:Webhooks.Events.orderCreated', 'Order created');
		case WebhookEvent.ORDER_UPDATED:
			return t('common:Webhooks.Events.orderUpdated', 'Order updated');
		case WebhookEvent.ORDER_PREPARED:
			return t('common:Webhooks.Events.orderPrepared', 'Order prepared');
		case WebhookEvent.ORDER_STARTED:
			return t('common:Webhooks.Events.orderStarted', 'Order started');
		case WebhookEvent.ORDER_RETURNED:
			return t('common:Webhooks.Events.orderReturned', 'Order returned');
		case WebhookEvent.PRODUCT_CREATED:
			return t('common:Webhooks.Events.productCreated', 'Product created');
		case WebhookEvent.PRODUCT_UPDATED:
			return t('common:Webhooks.Events.productUpdated', 'Product updated');
		case WebhookEvent.PRODUCT_DELETED:
			return t('common:Webhooks.Events.productDeleted', 'Product deleted');
	}
};

export const getPayloadTypeForWebhookEvent = (event: WebhookEvent) => {
	switch (event) {
		case WebhookEvent.ORDER_CREATED:
		case WebhookEvent.ORDER_UPDATED:
		case WebhookEvent.ORDER_PREPARED:
		case WebhookEvent.ORDER_STARTED:
		case WebhookEvent.ORDER_RETURNED:
			return 'Order';
		case WebhookEvent.PRODUCT_CREATED:
		case WebhookEvent.PRODUCT_UPDATED:
		case WebhookEvent.PRODUCT_DELETED:
			return 'Product';
	}
};

export const validateWebhookURL = (
	_url: string,
	options?: {
		blacklist?: string[];
	},
): WebhookInvalidReason | null => {
	if (options?.blacklist?.includes(_url)) return WebhookInvalidReason.URL_BLACKLISTED;

	try {
		const url = new URL(_url);

		if (url.protocol !== 'https:') {
			return WebhookInvalidReason.URL_NOT_HTTPS;
		}
		return null;
	} catch (e) {
		return WebhookInvalidReason.URL_NOT_VALID;
	}
};

export const validateWebhookEvent = (
	event?: WebhookEvent,
	options?: { blacklist?: WebhookEvent[] },
) => {
	if (!event || !Object.keys(WebhookEvent).includes(event))
		return WebhookInvalidReason.EVENT_NOT_VALID;
	if (options?.blacklist?.includes(event)) return WebhookInvalidReason.EVENT_BLACKLISTED;
	return null;
};

export const validateWebhookLabel = (
	label?: string,
	options?: { blacklist?: string[] },
): WebhookInvalidReason | null => {
	if (!label || label.length < 1) return WebhookInvalidReason.LABEL_NOT_EXISTING;
	if (label.length > 200) return WebhookInvalidReason.LABEL_TOO_LONG;
	if (options?.blacklist?.includes(label)) return WebhookInvalidReason.LABEL_BLACKLISTED;

	return null;
};

type WebhookValidationResult =
	| {
			isValid: false;
			reason: WebhookInvalidReason;
	  }
	| {
			isValid: true;
			data: WebhookInfo;
	  };

export const validateWebhook = (
	webhook: Partial<WebhookInfo>,
	options?: {
		blacklistedURLs?: string[];
		blacklistedLabels?: string[];
		blacklistedEvents?: WebhookEvent[];
	},
): WebhookValidationResult => {
	try {
		if (!webhook.urls || !webhook.urls.length)
			throw new Error(WebhookInvalidReason.URLS_NOT_EXISTING);
		if (webhook.urls.length > 5) throw new Error(WebhookInvalidReason.TOO_MANY_URLS);
		webhook.urls.forEach((url) => {
			const urlError = validateWebhookURL(url, { blacklist: options?.blacklistedURLs ?? [] });
			if (urlError) {
				throw new Error(urlError);
			}
		});

		const labelError = validateWebhookLabel(webhook.label, {
			blacklist: options?.blacklistedLabels ?? [],
		});
		if (labelError) {
			throw new Error(labelError);
		}

		const eventError = validateWebhookEvent(webhook.event, {
			blacklist: options?.blacklistedEvents ?? [],
		});
		if (eventError) {
			throw new Error(eventError);
		}

		return {
			isValid: true,
			data: webhook as WebhookInfo,
		};
	} catch (e) {
		if (Object.keys(WebhookInvalidReason).includes(e.message)) {
			return {
				isValid: false,
				reason: e.message as WebhookInvalidReason,
			};
		}
		return {
			isValid: false,
			reason: WebhookInvalidReason.UNKNOWN_ERROR,
		};
	}
};
